import React from 'react';

import { produce } from 'immer';
import { last } from 'lodash-es';
import { useGate } from 'statsig-react';
import { gql } from '@soundxyz/gql-string';
import type { OnData } from '@soundxyz/graphql-react-ws';
import { uuidv4 } from '@soundxyz/utils';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { useSubscription } from '@graphql/graphql-ws';
import type {
  ReleaseCollectorCursorConnectionSort,
  TopAudienceHookQuery,
} from '@graphql/reactQuery';
import {
  invalidateOperations,
  makeFragmentData,
  ReleaseTopCollectorsDocument,
  TopAudienceHookDocument,
  TopAudienceSeatFragmentDoc,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';

import { useSubscribeReleaseCollector } from '@hooks/useSubscribeRelease';
import { FEATURE_GATES } from '@utils/constants';
import { MOBILE_MAX_SEATS, TABLET_MAX_SEATS, TABLET_SIZE } from '../shared/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  subscription ReleaseTopCollectors($releaseId: UUID!) {
    releaseCollectorUpdates(releaseId: $releaseId) {
      releaseId
      collector {
        id
        nftsCount
        lowestNftSerialNumber
        mostRecentCommentMessage
        goldenEggSerialNumber
        gaNftsCount
        vipNftsCount
        userAvatarUrl
        userId
        userPublicAddress
        userWebappUri
        username
      }
    }
  }

  query TopAudienceHook($releaseId: UUID!, $pagination: ReleaseCollectorCursorConnectionArgs!) {
    release(id: $releaseId) {
      id
      collectors(pagination: $pagination) {
        edges {
          node {
            id
            nftsCount
            lowestNftSerialNumber
            user {
              id
              publicAddress
            }
            ...TopAudienceSeat
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export type TopAudienceCollector = NonNullable<
  TopAudienceHookQuery['release']
>['collectors']['edges'][number]['node'];

function useSubscriptReleaseTopCollectors({
  releaseId,
  onData,
}: {
  releaseId: string;
  onData: OnData<typeof ReleaseTopCollectorsDocument>;
}) {
  const { value: enabledSubscriptions } = useGate(FEATURE_GATES.TOP_AUDIENCE_SUBSCRIPTIONS);

  return useSubscription({
    query: ReleaseTopCollectorsDocument,
    variables: {
      releaseId,
    },
    onData,
    enabled: enabledSubscriptions,
  });
}

export function useTopAudience({
  releaseId,
  paginationCount,
  sort,
}: {
  releaseId: string;
  paginationCount: number;
  sort: ReleaseCollectorCursorConnectionSort;
}) {
  const logError = useLogError();
  const maxSeats =
    typeof window !== 'undefined' && window?.innerWidth < TABLET_SIZE
      ? MOBILE_MAX_SEATS
      : TABLET_MAX_SEATS;

  const {
    orderedList: collectors,
    error,
    isLoadingNewPage: loading,
    loadMoreNextPage: fetchMore,
    hasNextPage = false,
    entityStore,
    setInfiniteQueryData,
  } = useInfiniteQuery(TopAudienceHookDocument, {
    filterQueryKey: {
      releaseId,
      sort,
    },
    getNextPageParam({ data }) {
      return (
        data.release?.collectors.pageInfo.hasNextPage && {
          after: data.release.collectors.pageInfo.endCursor,
        }
      );
    },
    variables({ pageParam }) {
      return {
        releaseId,
        pagination: {
          sort,
          first: paginationCount,
          after: pageParam?.after || null,
        },
      };
    },
    list({ release }) {
      return release?.collectors.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    order: [
      [v => v.nftsCount, v => v.lowestNftSerialNumber, v => v.id],
      ['desc', 'asc', 'asc'],
    ],
    onError(error) {
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.AUDIENCE_FETCH_ERROR,
        error,
        errorType: ERROR_TYPE.FETCH_GQL_ERROR,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error calling TopAudienceHook',
        feature: FEATURES.AUDIENCE,
        indexedTags: {
          releaseId,
        },
      });
    },
    staleTime: 0,
  });

  useSubscriptReleaseTopCollectors({
    releaseId,
    onData({ data }) {
      if (!data.releaseCollectorUpdates.collector) return;

      const collectorFragment = makeFragmentData(
        {
          id: data.releaseCollectorUpdates.collector.id,
          mostRecentNftWithComment:
            data.releaseCollectorUpdates.collector.mostRecentCommentMessage != null
              ? {
                  id: uuidv4(),
                  comment: {
                    id: uuidv4(),
                    message: data.releaseCollectorUpdates.collector.mostRecentCommentMessage,
                  },
                }
              : null,
          nftsCount: data.releaseCollectorUpdates.collector.nftsCount,
          ownedGoldenEgg:
            data.releaseCollectorUpdates.collector.goldenEggSerialNumber != null
              ? {
                  id: uuidv4(),
                }
              : null,
          user: {
            id: data.releaseCollectorUpdates.collector.userId,
            username: data.releaseCollectorUpdates.collector.username,
            avatar: data.releaseCollectorUpdates.collector.userAvatarUrl
              ? {
                  id: uuidv4(),
                  url: data.releaseCollectorUpdates.collector.userAvatarUrl,
                }
              : null,
            publicAddress: data.releaseCollectorUpdates.collector.userPublicAddress,
            webappUri: data.releaseCollectorUpdates.collector.userWebappUri,
          },
          gaNftsCount: data.releaseCollectorUpdates.collector.gaNftsCount,
          vipNftsCount: data.releaseCollectorUpdates.collector.vipNftsCount,
        },
        TopAudienceSeatFragmentDoc,
      );

      // Update the entity store with the new value from subscriptions
      const collectorNode = (entityStore.nodes[data.releaseCollectorUpdates.collector.id] = {
        id: data.releaseCollectorUpdates.collector.id,
        lowestNftSerialNumber: data.releaseCollectorUpdates.collector.lowestNftSerialNumber,
        nftsCount: data.releaseCollectorUpdates.collector.nftsCount,
        user: {
          id: data.releaseCollectorUpdates.collector.id,
          publicAddress: data.releaseCollectorUpdates.collector.userPublicAddress,
        },
        ...collectorFragment,
      });

      /**
       * If the user is at the bottom, just add it at the end, we have client-side sorting
       * It should added at the last page because that's the last page to be revalidated
       */
      if (!hasNextPage) {
        setInfiniteQueryData(previousData => {
          if (!previousData) return previousData;

          return produce(previousData, draft => {
            last(draft.pages)?.data.release?.collectors.edges.push({
              node: collectorNode,
            });
          });
        });
      }

      // Invalidate the data
      invalidateOperations({
        operations: [TopAudienceHookDocument],
      });
    },
  });

  const { value: enabledSubscriptions } = useGate(FEATURE_GATES.TOP_AUDIENCE_SUBSCRIPTIONS);

  useSubscribeReleaseCollector({
    releaseId,
    onData({ data: { releaseCollectorUpdates } }) {
      setInfiniteQueryData(previousData => {
        return produce(previousData, draft => {
          if (!previousData || !draft) return;

          for (const page of draft.pages) {
            if (!page.data.release) continue;

            const targetIndex = page.data.release.collectors.edges.findIndex(
              collector =>
                collector.node.user.publicAddress === releaseCollectorUpdates.userPublicAddress,
            );

            if (targetIndex !== -1) {
              const targetCollector = page.data.release.collectors.edges[targetIndex];

              if (!targetCollector) continue;

              if (releaseCollectorUpdates.collector) {
                targetCollector.node.lowestNftSerialNumber =
                  releaseCollectorUpdates.collector.lowestNftSerialNumber;
                targetCollector.node.nftsCount = releaseCollectorUpdates.collector.nftsCount;
              } else {
                page.data.release.collectors.edges.splice(targetIndex, 1);
              }

              // If the collector was already in any page, just return early
              return;
            }
          }

          // If the collector was not found, let's just revalidate
          invalidateOperations({
            operations: [TopAudienceHookDocument],
          });
        });
      });
    },
    enabled: enabledSubscriptions,
  });

  const initialCollectors = React.useMemo(() => {
    return collectors.slice(0, maxSeats);
  }, [collectors, maxSeats]);

  const restOfCollectors = React.useMemo(() => {
    return collectors.slice(maxSeats);
  }, [collectors, maxSeats]);

  return {
    collectors,
    initialCollectors,
    restOfCollectors,
    loading,
    hasNextPage,
    fetchMore,
    error,
  };
}
